import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Button, IconButton, Tooltip } from "@material-tailwind/react";
import { Link } from 'react-router-dom';
import ReactSelect from 'react-select';
import { FaInfoCircle, FaChevronLeft, FaChevronRight, FaFileExport, FaBook, FaEye, FaUndoAlt } from 'react-icons/fa';
import axios from 'axios';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import ReverseTransactionDialog from './ReverseTransactionDialog';
import OwnerTransactionPreviewDialog from './OwnerTransactionPreviewDialog';

const OwnerTransactionsList = () => {
    const { token, user } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [owners, setOwners] = useState([]);
    const [transTypes, setTransTypes] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [loading, setLoading] = useState(false);

    // Initialize filters with current week (Monday to Today)
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(today);
        monday.setDate(diff);

        return {
            owner_code: '',
            trans_type: '',
            plate_no: '',
            week_key: '',
            date_from: monday.toISOString().split('T')[0],
            date_to: new Date().toISOString().split('T')[0]
        };
    });

    const [summary, setSummary] = useState({
        totalDebit: 0,
        totalCredit: 0,
        balance: 0
    });

    const [typeSummaries, setTypeSummaries] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState('');
    const [showGLPostModal, setShowGLPostModal] = useState(false);
    const [selectedTransactionForGL, setSelectedTransactionForGL] = useState(null);
    const [glPostingMessage, setGLPostingMessage] = useState({ type: '', text: '' });
    const [postingLoader, setPostingLoader] = useState(false);
    const [documentOpen, setDocumentOpen] = useState(false);
    const [selectedDocumentTrans, setSelectedDocumentTrans] = useState(null);
    const [reverseOpen, setReverseOpen] = useState(false);
    const [selectedTrans, setSelectedTrans] = useState(null);

    // Reset to page 1 when filters change or itemsPerPage changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, itemsPerPage]);

    // Format date to "Jan 12, 2026"
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const calculateSummary = (data) => {
        let debit = 0;
        let credit = 0;
        const typeSum = {};

        data.forEach(t => {
            const d = parseFloat(t.debit || 0);
            const c = parseFloat(t.credit || 0);
            debit += d;
            credit += c;

            // Type Summary
            const typeName = t.trans_type || 'Unknown';
            if (!typeSum[typeName]) {
                typeSum[typeName] = {
                    debit: 0,
                    credit: 0,
                    net: 0
                };
            }
            typeSum[typeName].debit += d;
            typeSum[typeName].credit += c;
            typeSum[typeName].net += (d - c);
        });

        setSummary({
            totalDebit: debit,
            totalCredit: credit,
            balance: debit - credit
        });
        setTypeSummaries(typeSum);
    };

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            let query = `${rootUrl}/api/owners/transactions?limit=10000`;
            if (filters.owner_code) query += `&owner_code=${filters.owner_code}`;
            if (filters.trans_type) query += `&trans_type=${filters.trans_type}`;
            if (filters.plate_no) query += `&plate_no=${filters.plate_no}`;
            if (filters.week_key) query += `&week_key=${filters.week_key}`;
            if (filters.date_from) query += `&date_from=${filters.date_from}`;
            if (filters.date_to) query += `&date_to=${filters.date_to}`;

            const res = await axios.get(query, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                setTransactions(res.data.data);
                calculateSummary(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching owner transactions:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const res = await axios.get(`${rootUrl}/owners`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOwners(res.data || []);  // Changed from res.data to res.data.data
            } catch (error) {
                console.error('Error fetching owners:', error);
            }
        };

        const fetchTransTypes = async () => {
            try {
                const res = await axios.get(`${rootUrl}/payments/owner-types`, {  // Changed endpoint path
                    headers: { Authorization: `Bearer ${token}` }
                });
                setTransTypes(res.data.data || []);  // Also access data wrapper
            } catch (error) {
                console.error('Error fetching transaction types:', error);
            }
        };

        const fetchWeeks = async () => {
            try {
                const res = await axios.get(`${rootUrl}/system/weeks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.success) {
                    setWeeks(res.data.data);
                }
            } catch (error) {
                console.error('Error fetching weeks:', error);
            }
        };

        fetchOwners();
        fetchTransTypes();
        fetchWeeks();
        fetchTransactions();
    }, [fetchTransactions, token]);

    const handleFilterChange = async (name, value) => {
        if (name === 'week_key' && value && value !== '') {
            // Find the selected week from the weeks array
            const selectedWeek = weeks.find(w => w.week === value);
            if (selectedWeek) {
                // Set the date range based on the week's start and end dates
                setFilters(prev => ({
                    ...prev,
                    week_key: value,
                    date_from: new Date(selectedWeek.start_date).toISOString().split('T')[0],
                    date_to: new Date(selectedWeek.end_date).toISOString().split('T')[0]
                }));
                return;
            }
        } else if (name === 'week_key' && (!value || value === '')) {
            // Reset to current week dates when "All Weeks" is selected
            const today = new Date();
            const day = today.getDay();
            const diff = today.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(today);
            monday.setDate(diff);

            setFilters(prev => ({
                ...prev,
                week_key: '',
                date_from: monday.toISOString().split('T')[0],
                date_to: new Date().toISOString().split('T')[0]
            }));
            return;
        }

        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const exportToCSV = () => {
        const headers = ['Date', 'Owner Code', 'Owner Name', 'Type', 'Plate No', 'Description', 'Debit', 'Credit', 'Week'];
        const rows = transactions.map(t => [
            formatDate(t.date),
            t.owner_code || '',
            t.owner_name || '',
            t.trans_type || '',
            t.plate_no || '',
            (t.description || '').replace(/,/g, ';'),
            t.debit || 0,
            t.credit || 0,
            t.week_key || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `owner_transactions_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const postTransactionToGL = async () => {
        if (!selectedTransactionForGL) return;

        try {
            setPostingLoader(true);
            setGLPostingMessage({ type: '', text: '' });

            const response = await axios.post(
                `${rootUrl}/api/owners/${selectedTransactionForGL.owner_code}/transactions/${selectedTransactionForGL.id}/post-gl`,
                { posted_by: user?.username },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setGLPostingMessage({ type: 'success', text: 'Transaction posted to GL successfully' });
                // We don't close immediately so the user can see the success message
                setTimeout(() => {
                    setShowGLPostModal(false);
                    fetchTransactions(); // Refresh to update GL status
                    setGLPostingMessage({ type: '', text: '' });
                }, 1500);
            } else {
                setGLPostingMessage({ type: 'error', text: response.data.message || 'Error posting transaction' });
            }
        } catch (error) {
            setGLPostingMessage({
                type: 'error',
                text: error.response?.data?.message || 'Error posting transaction to GL'
            });
        } finally {
            setPostingLoader(false);
        }
    };

    const openDocumentPreview = (transaction) => {
        setSelectedDocumentTrans(transaction);
        setDocumentOpen(true);
    };

    const closeDocumentPreview = () => {
        setDocumentOpen(false);
        setSelectedDocumentTrans(null);
    };

    const reversedOriginalIds = new Set(
        (transactions || [])
            .filter(t => t.reference_type === 'REVERSAL' && t.reference_id)
            .map(t => t.reference_id)
    );

    const reversalTypes = new Set(['REV', 'RRM', 'RPY', 'RRI', 'RCG', 'RAD', 'RJI']);

    const canReverse = (transaction) => {
        if (!transaction) return false;
        if (reversalTypes.has(String(transaction.trans_type || '').toUpperCase())) return false;
        if (reversedOriginalIds.has(transaction.id)) return false;
        return true;
    };

    const isReversedDocument = (transaction) => (
        transaction?.reversed === true
        || transaction?.reversed === 1
        || transaction?.reversed === '1'
        || String(transaction?.reference_type || '').toUpperCase() === 'REVERSAL'
    );

    const getDocumentNumber = (value) => (
        String(value || '').split(':').pop() || value || '-'
    );

    const openReverse = (transaction) => {
        setSelectedTrans(transaction);
        setReverseOpen(true);
    };

    const closeReverse = () => {
        setReverseOpen(false);
        setSelectedTrans(null);
    };

    const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="w-full max-w-7xl mx-auto px-4 py-6 pb-8">

                {/* Page Header */}
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h1 className="text-2xl font-bold text-[#2a2e30]">Owner Transactions</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Track and manage owner financial records</p>
                    </div>
                    <Button
                        onClick={exportToCSV}
                        disabled={transactions.length === 0}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none flex items-center gap-2 text-xs px-3 py-2"
                    >
                        <FaFileExport className="w-3 h-3" /> Export CSV
                    </Button>
                </div>

                {/* Summary Strip */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                    {[
                        { label: 'Total Debit', value: fmt(summary.totalDebit), color: 'text-red-600' },
                        { label: 'Total Credit', value: fmt(summary.totalCredit), color: 'text-green-600' },
                        { label: 'Net Balance', value: fmt(summary.balance), color: summary.balance >= 0 ? 'text-green-600' : 'text-red-600' },
                    ].map(({ label, value, color }) => (
                        <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3">
                            <span className="block text-xs font-bold text-gray-400 uppercase tracking-wide">{label}</span>
                            <span className={`block text-xl font-bold mt-0.5 ${color}`}>{value}</span>
                        </div>
                    ))}
                </div>

                {/* Main Card */}
                <Card className="bg-white rounded-2xl shadow-xl">
                    <CardBody className="p-4">

                        {/* Filters */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Owner</label>
                                <ReactSelect
                                    value={filters.owner_code ? { value: filters.owner_code, label: owners.find(o => o.code === filters.owner_code)?.name || filters.owner_code } : null}
                                    onChange={(opt) => handleFilterChange('owner_code', opt?.value || '')}
                                    options={[{ value: '', label: 'All Owners' }, ...owners.map(o => ({ value: o.code, label: `${o.name} (${o.code})` }))]}
                                    isClearable classNamePrefix="react-select" placeholder="All owners..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                                <ReactSelect
                                    value={filters.trans_type ? { value: filters.trans_type, label: transTypes.find(t => t.type === filters.trans_type)?.long_desc || filters.trans_type } : null}
                                    onChange={(opt) => handleFilterChange('trans_type', opt?.value || '')}
                                    options={[{ value: '', label: 'All Types' }, ...transTypes.map(t => ({ value: t.type, label: t.long_desc }))]}
                                    isClearable classNamePrefix="react-select" placeholder="All types..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Week</label>
                                <ReactSelect
                                    value={filters.week_key ? { value: filters.week_key, label: weeks.find(w => w.week === filters.week_key)?.week } : null}
                                    onChange={(opt) => handleFilterChange('week_key', opt?.value || '')}
                                    options={[{ value: '', label: 'All Weeks' }, ...weeks.map(w => ({ value: w.week, label: `${w.week} (${new Date(w.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(w.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` }))]}
                                    isClearable classNamePrefix="react-select" placeholder="Select week..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">From</label>
                                <input
                                    type="date"
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none ${filters.week_key ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                    disabled={!!filters.week_key}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">To</label>
                                <input
                                    type="date"
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none ${filters.week_key ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    value={filters.date_to}
                                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                    disabled={!!filters.week_key}
                                />
                            </div>
                            <div className="flex items-end">
                                <Button
                                    onClick={fetchTransactions}
                                    className="w-full bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm px-4 h-[38px] shadow-none text-sm font-semibold"
                                >
                                    Filter
                                </Button>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto rounded-xl border border-gray-100">
                            <table className="w-full min-w-max table-auto text-left border-collapse">
                                <thead className="bg-slate-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">Date</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">Owner</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">Type</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">Plate</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">Description</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase text-right">Debit</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase text-right">Credit</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">Week</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase">GL</th>
                                        <th className="px-3 py-2.5 font-bold text-xs text-gray-500 uppercase text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="10" className="px-3 py-6 text-center text-sm text-gray-500">Loading...</td></tr>
                                    ) : transactions.length === 0 ? (
                                        <tr><td colSpan="10" className="px-3 py-8 text-center text-sm text-gray-400">No transactions found</td></tr>
                                    ) : (
                                        <>
                                            {transactions
                                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                                .map((t) => (
                                                    <tr key={t.id} className={`border-b border-gray-50 transition-colors ${isReversedDocument(t) ? 'bg-red-50/60 hover:bg-red-50' : 'hover:bg-slate-50'}`}>
                                                        <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{formatDate(t.date)}</td>
                                                        <td className="px-3 py-2">
                                                            <Link to={`/owners/statements/${t.owner_code}`} className="text-xs font-semibold text-blue-600 hover:underline">
                                                                {t.owner_name || t.owner_code}
                                                            </Link>
                                                            <div className="text-[10px] text-gray-400">{t.owner_code}</div>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase">{t.trans_type}</span>
                                                        </td>
                                                        <td className="px-3 py-2 text-xs">
                                                            {t.plate_no ? (
                                                                <Link to={`/cars/${t.plate_no}`} className="font-semibold text-blue-600 hover:underline">{t.plate_no}</Link>
                                                            ) : <span className="text-gray-300">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 max-w-[180px]">
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs text-gray-600 truncate">{t.description || <span className="text-gray-300">—</span>}</span>
                                                                {t.notes && (
                                                                    <Tooltip content={t.notes} placement="top">
                                                                        <button onClick={() => { setSelectedNote(t.notes); setShowNoteModal(true); }} className="shrink-0 text-blue-400 hover:text-blue-600">
                                                                            <FaInfoCircle className="w-3 h-3" />
                                                                        </button>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-right font-semibold text-red-600 whitespace-nowrap">
                                                            {parseFloat(t.debit || 0) > 0 ? fmt(t.debit) : <span className="text-gray-300">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-right font-semibold text-green-600 whitespace-nowrap">
                                                            {parseFloat(t.credit || 0) > 0 ? fmt(t.credit) : <span className="text-gray-300">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{t.week_key || <span className="text-gray-300">—</span>}</td>
                                                        <td className="px-3 py-2">
                                                            {t.gl_posted ? (
                                                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">Posted</span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => { setSelectedTransactionForGL(t); setShowGLPostModal(true); }}
                                                                    className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded text-[10px] font-bold transition-colors"
                                                                >
                                                                    <FaBook className="w-2.5 h-2.5" /> Post
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex items-center justify-center gap-1.5">
                                                                <Link
                                                                    to={`/owners/transactions/${encodeURIComponent(t.owner_code)}/${encodeURIComponent(t.id)}`}
                                                                    className="font-mono text-[10px] font-bold text-[#FF5733] hover:underline"
                                                                    title="Open transaction"
                                                                >
                                                                    {getDocumentNumber(t.id)}
                                                                </Link>
                                                                {isReversedDocument(t) && (
                                                                    <span className="px-1 py-0.5 bg-red-100 text-red-600 rounded text-[9px] font-bold uppercase">Rev</span>
                                                                )}
                                                                <Tooltip content="Preview document" placement="top">
                                                                    <button onClick={() => openDocumentPreview(t)} className="h-6 w-6 flex items-center justify-center rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition">
                                                                        <FaEye className="w-3 h-3" />
                                                                    </button>
                                                                </Tooltip>
                                                                {canReverse(t) ? (
                                                                    <Tooltip content="Reverse" placement="top">
                                                                        <button onClick={() => openReverse(t)} className="h-6 w-6 flex items-center justify-center rounded bg-red-50 text-red-600 hover:bg-red-100 transition">
                                                                            <FaUndoAlt className="w-3 h-3" />
                                                                        </button>
                                                                    </Tooltip>
                                                                ) : null}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            <tr className="bg-slate-50 border-t-2 border-gray-200 font-bold">
                                                <td colSpan="5" className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Totals</td>
                                                <td className="px-3 py-2 text-xs text-right text-red-700">{fmt(summary.totalDebit)}</td>
                                                <td className="px-3 py-2 text-xs text-right text-green-700">{fmt(summary.totalCredit)}</td>
                                                <td colSpan="3" className="px-3 py-2"></td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {transactions.length > 0 && (
                            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-500">
                                        {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, transactions.length)} of {transactions.length}
                                    </span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                                        className="px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                    >
                                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <IconButton size="sm" variant="outlined" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="rounded-sm border-gray-300 h-7 w-7">
                                        <FaChevronLeft className="w-3 h-3" />
                                    </IconButton>
                                    <span className="text-xs text-gray-500 px-2">Page {currentPage} of {Math.max(1, Math.ceil(transactions.length / itemsPerPage))}</span>
                                    <IconButton size="sm" variant="outlined" onClick={() => setCurrentPage(p => Math.min(Math.ceil(transactions.length / itemsPerPage), p + 1))} disabled={currentPage >= Math.ceil(transactions.length / itemsPerPage)} className="rounded-sm border-gray-300 h-7 w-7">
                                        <FaChevronRight className="w-3 h-3" />
                                    </IconButton>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Type Summary */}
                {Object.keys(typeSummaries).length > 0 && (
                    <Card className="bg-white rounded-2xl shadow-xl mt-4">
                        <CardBody className="p-4">
                            <h2 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">By Transaction Type</h2>
                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">Type</th>
                                            <th className="px-3 py-2 text-xs font-bold text-gray-500 uppercase text-right">Debit</th>
                                            <th className="px-3 py-2 text-xs font-bold text-gray-500 uppercase text-right">Credit</th>
                                            <th className="px-3 py-2 text-xs font-bold text-gray-500 uppercase text-right">Net</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(typeSummaries).map(([type, data]) => (
                                            <tr key={type} className="border-b border-gray-50 hover:bg-slate-50">
                                                <td className="px-3 py-2"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase">{type}</span></td>
                                                <td className="px-3 py-2 text-xs text-right text-red-600 font-semibold">{fmt(data.debit)}</td>
                                                <td className="px-3 py-2 text-xs text-right text-green-600 font-semibold">{fmt(data.credit)}</td>
                                                <td className={`px-3 py-2 text-xs text-right font-bold ${data.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(data.net)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {documentOpen && selectedDocumentTrans && (
                    <OwnerTransactionPreviewDialog open={documentOpen} onClose={closeDocumentPreview} transaction={selectedDocumentTrans} />
                )}

                {reverseOpen && selectedTrans && (
                    <ReverseTransactionDialog
                        open={reverseOpen} onClose={closeReverse}
                        ownerCode={selectedTrans.owner_code} transaction={selectedTrans}
                        onSuccess={() => { closeReverse(); fetchTransactions(); }}
                    />
                )}

                {/* Note Modal */}
                {showNoteModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNoteModal(false)}>
                        <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-[#2a2e30]">Transaction Note</h3>
                                <button onClick={() => setShowNoteModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-80 overflow-y-auto">{selectedNote}</p>
                            <div className="mt-5 flex justify-end">
                                <Button onClick={() => setShowNoteModal(false)} className="bg-[#FF5733] text-white rounded-sm shadow-none px-4 py-1.5 text-sm">Close</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* GL Post Modal */}
                {showGLPostModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-[#2a2e30]">Post to General Ledger</h3>
                                <button onClick={() => setShowGLPostModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
                            </div>
                            {glPostingMessage.text && (
                                <div className={`mb-4 p-3 rounded-lg text-sm font-semibold ${glPostingMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                    {glPostingMessage.text}
                                </div>
                            )}
                            {selectedTransactionForGL && (
                                <div className="space-y-2 bg-gray-50 rounded-xl p-4 border border-gray-100 mb-5 text-sm">
                                    {[
                                        ['ID', selectedTransactionForGL.id],
                                        ['Owner', selectedTransactionForGL.owner_name || selectedTransactionForGL.owner_code],
                                        ['Type', selectedTransactionForGL.trans_type],
                                        ['Date', formatDate(selectedTransactionForGL.date)],
                                        ['Amount', fmt(selectedTransactionForGL.debit)],
                                    ].map(([label, val]) => (
                                        <div key={label} className="flex justify-between">
                                            <span className="text-xs font-bold text-gray-400 uppercase">{label}</span>
                                            <span className="text-xs font-semibold text-gray-700">{val}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-3">
                                <Button onClick={() => setShowGLPostModal(false)} disabled={postingLoader} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none font-bold">Cancel</Button>
                                <Button onClick={postTransactionToGL} disabled={postingLoader} className="flex-1 bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm shadow-none font-bold">
                                    {postingLoader ? 'Posting...' : 'Confirm Post'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OwnerTransactionsList;
